/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module HttpPolicySetModule
 */

import {
    Component,
    Type,
} from '@angular/core';

import { HTTPPolicySet } from 'object-types';
import { L10nService } from '@vmw/ngx-vip';

import {
    AviPermissionResource,
    IHTTPPolicySet,
    IHTTPSecurityRule,
} from 'generated-types';

import { MessageItem }
    from 'ajs/modules/data-model/factories/message-item.factory';

import { RepeatedMessageItem }
    from 'ajs/modules/data-model/factories/repeated-message-item.factory';

import { ObjectTypeItem } from 'ajs/modules/data-model/factories/object-type-item.factory';
import { withFullModalMixin } from 'ajs/js/utilities/mixins/with-full-modal.mixin';
import { TWindowElement } from 'ajs/modules/data-model/data-model.types';
import { withEditChildMessageItemMixin }
    from 'ajs/modules/data-model/mixins/with-edit-child-message-item.mixin';

import { HTTPSecurityPolicyConfigItem } from './http-security-policy.config-item.factory';

import { HTTPRequestPolicyConfigItem }
    from './http-request/http-request-policy.config-item.factory';

import { HTTPRequestRuleConfigItem } from './http-request/http-request-rule.config-item.factory';

import { HTTPResponsePolicyConfigItem }
    from './http-response/http-response-policy.config-item.factory';

import { HTTPResponseRuleConfigItem } from './http-response/http-response-rule.config-item.factory';

import * as l10n from '../http-policy-set.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

type THTTPPolicySetRulesPartial = Omit<IHTTPPolicySet,
'http_security_policy' | 'http_request_policy' | 'http_response_policy'>;

interface IHTTPPolicySetRulesConfig extends THTTPPolicySetRulesPartial {
    http_security_policy?: RepeatedMessageItem<HTTPSecurityPolicyConfigItem>;
    http_request_policy?: RepeatedMessageItem<HTTPRequestPolicyConfigItem>;
    http_response_policy?: RepeatedMessageItem<HTTPResponsePolicyConfigItem>;
}

interface IHTTPPolicySetData {
    config: IHTTPPolicySetRulesConfig;
}

/**
 * @description HttpPolicySet objectTypeItem.
 * @author Rohit Gaikwad, Abhinesh Gour
 */
export class HTTPPolicySetItem extends
    withEditChildMessageItemMixin(withFullModalMixin(ObjectTypeItem)) {
    public static ajsDependencies = [
        'l10nService',
    ];

    /**
     * HTTPPolicySet data.
     */
    public data: IHTTPPolicySetData;

    private readonly l10nService: L10nService;

    constructor(args = {}) {
        const extendedArgs = {
            objectName: 'httppolicyset',
            objectType: HTTPPolicySet,
            permissionName: AviPermissionResource.PERMISSION_HTTPPOLICYSET,
            windowElement: 'lazy-load',
            ...args,
        };

        super(extendedArgs);

        this.l10nService = this.getAjsDependency_('l10nService');
        this.l10nService.registerSourceBundles(dictionary);
    }

    /**
     * Import Lazy Loaded Modal Component.
     */
    /* eslint-disable-next-line */
    public async getModalComponent(windowElement: TWindowElement): Promise<Type<Component>> {
        const { HTTPPolicySetModalComponent } = await import(
            /* webpackChunkName: "http-policy-set" */
            'ng/lazy-loaded-components/modals/http-policy-set-modal/http-policy-set-modal.component'
        );

        return HTTPPolicySetModalComponent as Type<Component>;
    }

    /**
     * Return if the HTTPPolicySet contains rules.
     */
    public hasRules(): boolean {
        return !this.httpSecurityPolicy.rules.isEmpty() ||
            !this.httpRequestPolicy.rules.isEmpty() ||
            !this.httpResponsePolicy.rules.isEmpty();
    }

    /**
     * Getter for httpSecurityPolicy.
     */
    public get httpSecurityPolicy(): HTTPSecurityPolicyConfigItem {
        return this.config.http_security_policy;
    }

    /**
     * Opens HTTPPolicySet security modal to add new security rule.
     */
    public createSecurityRule(): void {
        this.httpSecurityPolicy.addSecurityRule();
    }

    /**
     * Opens HTTPPolicySet security modal to edit existing security rule.
     */
    public editSecurityRule(event: MessageItem<IHTTPSecurityRule>): void {
        this.httpSecurityPolicy.editSecurityRuleModal(event);
    }

    /**
     * Deletes the existing security rule.
     */
    public deleteSecurityRule(event: any): void {
        this.httpSecurityPolicy.config.rules.removeByMessageItem(event);
    }

    /**
     * Getter for HTTPRequestPolicyConfigItem.
     */
    public get httpRequestPolicy(): HTTPRequestPolicyConfigItem {
        return this.config.http_request_policy;
    }

    /**
     * Opens HTTPPolicySet Request modal to add new request rule.
     */
    public createRequestRule(): void {
        this.httpRequestPolicy.addRequestRule();
    }

    /**
     * Opens HTTPPolicySet Request modal to edit existing request rule.
     */
    public editRequestRule(event: HTTPRequestRuleConfigItem): void {
        this.httpRequestPolicy.editRequestRuleModal(event);
    }

    /**
     * Deletes the given request rule.
     */
    public deleteRequestRule(event: HTTPRequestRuleConfigItem): void {
        this.httpRequestPolicy.rules.removeByMessageItem(event);
    }

    /**
     * Getter for HTTPResponsePolicyConfigItem.
     */
    public get httpResponsePolicy(): HTTPResponsePolicyConfigItem {
        return this.config.http_response_policy;
    }

    /**
     * Opens HTTPPolicySet Response modal to add new Response Rule.
     */
    public createResponseRule(): void {
        this.httpResponsePolicy.addResponseRule();
    }

    /**
     * Opens HTTPPolicySet Response modal to edit existing Response rule.
     */
    public editResponseRule(event: HTTPResponseRuleConfigItem): void {
        this.httpResponsePolicy.editResponseRuleModal(event);
    }

    /**
     * Deletes the given Response rule.
     */
    public deleteResponseRule(event: HTTPResponseRuleConfigItem): void {
        this.httpResponsePolicy.rules.removeByMessageItem(event);
    }

    /** @override */
    // eslint-disable-next-line class-methods-use-this
    protected requiredFields(): string[] {
        return [
          'http_security_policy',
          'http_request_policy',
          'http_response_policy',
        ];
    }

    /**
     * @override
     */
    protected getModalBreadcrumbTitle(): string {
        return this.l10nService.getMessage(l10nKeys.httpPolicySetModalBreadcrumbTitle);
    }
}
